import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Card } from "react-bootstrap";

import { motion } from "framer-motion";
import showToast from "helpers/showToast";
import apiCall from "services/apiCall";
import { getPersonelByDomainType } from "services/personel-service";
import { getSuperAdmins, addSuperAdmin, deleteSuperAdmin } from "services/site-settings-service";
import IconLoader from "components/IconLoader/IconLoader";
import DataGridLoadingAnimation from "components/Animations/DataGridLoadingAnimation";
import AwsImage from "components/Files/AwsImage";

const SuperAdmins = () => {
  const [loading, setLoading] = useState(false);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [personnelList, setPersonnelList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState("");

  const getPersonPrimaryContact = useCallback((person) => {
    if (person.contacts?.length > 0) {
      return person.contacts.find((contact) => !contact.deleted) || person.contacts[0];
    }
    return null;
  }, []);

  const getPersonPrimaryRole = useCallback((person) => {
    if (!person.roles?.length) return null;

    const activeRoles = person.roles.filter((role) => !role.deleted && role.status === 1);
    if (!activeRoles.length) return null;

    const roleOrder = ["SCHOOL", "CAMPUS", "CORPORATION"];

    for (const domainType of roleOrder) {
      const role = activeRoles.find((r) => r.domainType === domainType);
      if (role) return role;
    }

    return activeRoles[0];
  }, []);

  const filteredPersonnel = useMemo(() => {
    if (!searchText) return personnelList;

    const searchLower = searchText.toLowerCase();
    return personnelList.filter((person) => {
      const identity = person.identity || {};
      const fullName = `${identity.name || ""} ${identity.surname || ""}`.toLowerCase().trim();
      const identityNumber = identity.identityNumber || "";

      const primaryContact = getPersonPrimaryContact(person);
      const phone = primaryContact?.phone?.[0] || "";
      const email = primaryContact?.mail?.[0] || "";

      const primaryRole = getPersonPrimaryRole(person);
      const roleName = primaryRole?.roleName || "";
      const domainName = primaryRole?.domainName || "";

      return (
        fullName.includes(searchLower) ||
        identityNumber.includes(searchLower) ||
        phone.includes(searchLower) ||
        email.toLowerCase().includes(searchLower) ||
        roleName.toLowerCase().includes(searchLower) ||
        domainName.toLowerCase().includes(searchLower)
      );
    });
  }, [searchText, personnelList, getPersonPrimaryContact, getPersonPrimaryRole]);

  const personnelToDisplay = useMemo(() => {
    return filteredPersonnel.filter(
      (person) => !superAdmins.some((admin) => admin.userId === person.id)
    );
  }, [filteredPersonnel, superAdmins]);

  const enrichSuperAdmins = useCallback((admins, personnel) => {
    if (!admins.length || !personnel.length) return admins;

    return admins.map((admin) => {
      const matchingPerson = personnel.find((person) => person.id === admin.userId);
      return matchingPerson ? { ...admin, ...matchingPerson } : admin;
    });
  }, []);

  const fetchSuperAdmins = useCallback(async () => {
    let result = [];
    try {
      await apiCall({
        service: getSuperAdmins,
        onSuccess: ({ data }) => {
          result = Array.isArray(data) ? data : [];
          setSuperAdmins(result);
        },
        onError: () => {
          showToast("Super admin listesi alınırken bir hata oluştu.", { type: "error" });
          setSuperAdmins([]);
        }
      });
    } catch (error) {
      setSuperAdmins([]);
    }
    return result;
  }, []);

  const fetchPersonnel = useCallback(async () => {
    let result = [];
    try {
      await apiCall({
        service: getPersonelByDomainType,
        serviceParams: "CORPORATION",
        onSuccess: ({ data }) => {
          if (data.list) {
            const validPersonnel = data.list.filter(
              (item) => item?.id && item?.identity && (item.identity.name || item.identity.surname)
            );
            result = validPersonnel;
            setPersonnelList(validPersonnel);
          } else {
            setPersonnelList([]);
          }
        },
        onError: () => {
          setPersonnelList([]);
          showToast("Personel listesi alınırken bir hata oluştu.", { type: "error" });
        }
      });
    } catch (error) {
      setPersonnelList([]);
    }
    return result;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [admins, personnel] = await Promise.all([fetchSuperAdmins(), fetchPersonnel()]);

      if (admins.length > 0 && personnel.length > 0) {
        setSuperAdmins(enrichSuperAdmins(admins, personnel));
      }
    } finally {
      setLoading(false);
    }
  }, [fetchSuperAdmins, fetchPersonnel, enrichSuperAdmins]);

  const handleAddSuperAdmin = useCallback((person) => {
    if (!person?.id || !person?.identity) {
      showToast("Geçersiz personel verisi.", { type: "error" });
      return;
    }

    setSubmitting(true);
    apiCall({
      service: addSuperAdmin,
      serviceParams: { userId: person.id },
      onSuccess: () => {
        setSuperAdmins((prev) => [...prev, { userId: person.id, ...person }]);
        showToast("Super admin başarıyla eklendi.", { type: "success" });
      },
      onError: () => {
        showToast("Super admin eklenirken bir hata oluştu.", { type: "error" });
      },
      onFinally: () => setSubmitting(false)
    });
  }, []);

  const handleRemoveSuperAdmin = useCallback((userId) => {
    if (!userId) return;

    setSubmitting(true);
    apiCall({
      service: deleteSuperAdmin,
      serviceParams: userId,
      onSuccess: () => {
        setSuperAdmins((prev) => prev.filter((admin) => admin.userId !== userId));
        showToast("Super admin başarıyla silindi.", { type: "success" });
      },
      onError: () => {
        showToast("Super admin silinirken bir hata oluştu.", { type: "error" });
      },
      onFinally: () => setSubmitting(false)
    });
  }, []);

  const getDisplayName = useCallback((identity) => {
    if (!identity) return "İsimsiz";
    const name = identity.name || "";
    const surname = identity.surname || "";
    return `${name} ${surname}`.trim() || "İsimsiz";
  }, []);

  const getGenderBasedAvatar = useCallback((gender) => {
    return gender === 0
      ? "/media/users/avatar5.png"
      : gender === 1
      ? "/media/users/avatar3.png"
      : "/media/users/default.jpg";
  }, []);

  const cardVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.02, boxShadow: "0 10px 20px rgba(0,0,0,0.1)" },
    tap: { scale: 0.98 }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const PersonnelItem = React.memo(({ person }) => {
    const identity = person.identity || {};
    const primaryContact = getPersonPrimaryContact(person);
    const primaryRole = getPersonPrimaryRole(person);
    const displayName = getDisplayName(identity);

    return (
      <div
        onClick={() => handleAddSuperAdmin(person)}
        className="bg-hover-light-info mb-2 d-flex p-2 rounded align-content-center align-items-center cursor-pointer"
      >
        <div
          className="rounded-circle mr-2 overflow-hidden shadow-sm ml-4"
          style={{ height: 36, width: 36 }}
        >
          <AwsImage
            sha1={identity.photo}
            thumb={identity.photo ? undefined : getGenderBasedAvatar(identity.gender)}
            className="lms-user-upload-image"
          />
        </div>
        <div className="d-flex flex-column flex-fill">
          <div className="d-flex justify-content-between align-items-center">
            <b>{displayName}</b>
            {primaryContact?.phone?.[0] && (
              <small className="text-muted">{primaryContact.phone[0]}</small>
            )}
          </div>
          {identity.identityNumber && (
            <small className="text-muted">TC: {identity.identityNumber}</small>
          )}
          {primaryRole && (
            <small className="text-muted">
              {primaryRole.roleName} - {primaryRole.domainName}
            </small>
          )}
          {primaryContact?.mail?.[0] && (
            <small className="text-success">{primaryContact.mail[0]}</small>
          )}
        </div>
      </div>
    );
  });

  const SuperAdminItem = React.memo(({ admin }) => {
    const primaryContact = admin.contacts?.length > 0 ? admin.contacts[0] : null;
    const primaryRole = admin.roles ? getPersonPrimaryRole({ roles: admin.roles }) : null;

    if (!admin.identity) return null;

    return (
      <div className="bg-hover-light-info mb-2 d-flex p-2 rounded justify-content-between align-items-center">
        <div className="d-flex flex-fill">
          <div
            className="rounded-circle mr-2 overflow-hidden shadow-sm ml-4"
            style={{ height: 36, width: 36 }}
          >
            <AwsImage
              sha1={admin.identity.photo}
              thumb={admin.identity.photo ? undefined : getGenderBasedAvatar(admin.identity.gender)}
              className="lms-user-upload-image"
            />
          </div>
          <div className="d-flex flex-column flex-fill">
            <div className="d-flex justify-content-between align-items-center">
              <b>{getDisplayName(admin.identity)}</b>
              {primaryContact?.phone?.[0] && (
                <small className="text-muted mr-3">{primaryContact.phone[0]}</small>
              )}
            </div>
            {admin.identity.identityNumber && (
              <small className="text-muted">TC: {admin.identity.identityNumber}</small>
            )}
            {primaryRole && (
              <small className="text-muted">
                {primaryRole.roleName} - {primaryRole.domainName}
              </small>
            )}
            {primaryContact?.mail?.[0] && (
              <small className="text-success">{primaryContact.mail[0]}</small>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-light-danger ml-2"
          onClick={() => handleRemoveSuperAdmin(admin.userId || admin.id)}
          disabled={submitting}
        >
          <IconLoader icon="mdiClose" size={1} />
        </button>
      </div>
    );
  });

  return (
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3 }}
    >
      <motion.div variants={cardVariants} initial="initial" whileHover="hover">
        <Card className="shadow-sm mb-0">
          <Card.Header className="bg-white py-3">
            <h5 className="mb-0">
              <IconLoader icon="mdiAccountMultiple" className="mr-2" size={1} />
              Süper Yöneticiler
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="d-flex border rounded p-2 bg-white mt-2 mb-2">
                  <IconLoader
                    icon="mdiMagnify"
                    className="d-block my-auto"
                    size={1.3}
                    style={{ fontWeight: "bold" }}
                  />
                  <input
                    className="flex-fill flex-grow-1 border-0 overflow-hidden ml-2"
                    style={{ outline: "none" }}
                    placeholder="İsim, TC, telefon, email veya role göre arayın..."
                    onChange={(e) => setSearchText(e.target.value)}
                    value={searchText}
                    autoFocus
                  />
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ minHeight: "24rem", maxHeight: "24rem" }}
                >
                  <label className="h4 text-info ml-4 mt-4">Personeller</label>
                  {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center my-4">
                      <DataGridLoadingAnimation height={200} width={200} />
                    </div>
                  ) : (
                    <div className="lms-scrollbar">
                      {personnelToDisplay.map((person) => (
                        <PersonnelItem key={person.id} person={person} />
                      ))}
                      {personnelToDisplay.length === 0 && (
                        <div className="text-center py-5">
                          <p className="text-muted">
                            {searchText
                              ? "Arama kriterlerine uygun personel bulunamadı."
                              : "Eklenebilecek personel bulunamadı."}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Col>
              <Col
                md={6}
                className="d-flex flex-column"
                style={{ minHeight: "24rem", maxHeight: "24rem" }}
              >
                <label className="h4 text-info ml-4 mt-4">Süper Yöneticiler</label>
                <div className="lms-scrollbar">
                  {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center my-4">
                      <DataGridLoadingAnimation height={200} width={200} />
                    </div>
                  ) : (
                    <>
                      {superAdmins.map((admin) => (
                        <SuperAdminItem key={admin.userId} admin={admin} />
                      ))}
                      {superAdmins.length === 0 && (
                        <div className="text-center py-5">
                          <p className="text-muted">Henüz süper yönetici eklenmemiş.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="mt-3 text-muted small text-center">
                  <p className="mb-0">
                    Süper yöneticiler, Marka bazında kısmi yönetim yapabilirler.
                  </p>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default SuperAdmins;
